import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { api } from '../../../api/client';
import InfoTip from '../../../components/InfoTip';
import ProviderKeyCard from '../../../components/ProviderKeyCard';
import { toast } from '../../../lib/toast';

export default function AIKeysSection() {
  const qc = useQueryClient();

  const aiKeysQuery = useQuery({
    queryKey: ['ai-keys'],
    queryFn: () => api.get('/api/me/ai-keys').then((r) => r.data),
  });
  const aiOptionsQuery = useQuery({
    queryKey: ['ai-options'],
    queryFn: () => api.get('/api/me/ai-options').then((r) => r.data),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ai-keys'] });
    qc.invalidateQueries({ queryKey: ['api-key'] });
  };

  const anthropicSaveMutation = useMutation({
    mutationFn: (key) => api.put('/api/me/api-key', { anthropicApiKey: key }).then((r) => r.data),
    onSuccess: (data) => {
      invalidate();
      if (data?.warning) toast.info(data.warning);
      else toast.success('Anthropic key saved');
    },
    onError: (err) => toast.error(err, 'Could not save Anthropic key'),
  });
  const anthropicDeleteMutation = useMutation({
    mutationFn: () => api.delete('/api/me/api-key'),
    onSuccess: () => {
      invalidate();
      toast.success('Anthropic key removed');
    },
    onError: (err) => toast.error(err, 'Could not remove Anthropic key'),
  });

  const openaiSaveMutation = useMutation({
    mutationFn: (key) => api.put('/api/me/openai-key', { openaiApiKey: key }).then((r) => r.data),
    onSuccess: (data) => {
      invalidate();
      if (data?.warning) toast.info(data.warning);
      else toast.success('OpenAI key saved');
      const anthropicActive = aiKeysQuery.data?.active === 'anthropic';
      if (anthropicActive && !aiKeysQuery.data?.preferred) {
        toast.info('Anthropic key takes priority — set OpenAI as preferred to use it instead');
      }
    },
    onError: (err) => toast.error(err, 'Could not save OpenAI key'),
  });
  const openaiDeleteMutation = useMutation({
    mutationFn: () => api.delete('/api/me/openai-key'),
    onSuccess: () => {
      invalidate();
      toast.success('OpenAI key removed');
    },
    onError: (err) => toast.error(err, 'Could not remove OpenAI key'),
  });

  const prefsMutation = useMutation({
    mutationFn: (prefs) => api.put('/api/me/ai-preferences', prefs).then((r) => r.data),
    onSuccess: () => {
      invalidate();
      toast.success('AI preferences saved');
    },
    onError: (err) => toast.error(err, 'Could not save preferences'),
  });

  const keys = aiKeysQuery.data;
  const options = aiOptionsQuery.data;

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Sparkles className="w-4 h-4" />
          </span>
          AI providers
          <InfoTip
            title="How this works"
            body="Pick a preferred provider and save the API key(s) you want to use. Auto-fallback lets us retry on the secondary provider if the primary fails (invalid key or no quota)."
          />
        </div>
        {keys?.active && (
          <span className="pill-success">
            Active: {keys.active === 'anthropic' ? 'Anthropic' : 'OpenAI'}
          </span>
        )}
      </div>

      <PreferencesForm
        preferred={keys?.preferred ?? null}
        enableAutoFallback={keys?.enableAutoFallback ?? false}
        saving={prefsMutation.isPending}
        onSave={(prefs) => prefsMutation.mutate(prefs)}
      />

      <div className="border-t border-gray-200 -mx-6 my-5" />

      <ProviderKeyCard
        embedded
        label="Anthropic API key"
        placeholder="sk-ant-..."
        hint={{
          title: 'Optional',
          body: "Route your AI calls through Claude on your own Anthropic bill. Get one at console.anthropic.com → API keys. When both Anthropic and OpenAI keys are saved and no preference is set, Anthropic takes priority.",
        }}
        description="Save an Anthropic key here to route AI features through Claude on your own bill. Stored against your account, encrypted at rest, never shared."
        hasKey={keys?.anthropic?.hasKey}
        preview={keys?.anthropic?.preview}
        isActive={keys?.active === 'anthropic'}
        isOverridden={keys?.anthropic?.hasKey && keys?.active && keys?.active !== 'anthropic'}
        createdAt={keys?.anthropic?.createdAt}
        lastUsedAt={keys?.anthropic?.lastUsedAt}
        model={keys?.anthropic?.model || options?.defaultAnthropicModel || ''}
        modelOptions={options?.anthropicModels || []}
        saving={anthropicSaveMutation.isPending}
        deleting={anthropicDeleteMutation.isPending}
        onSave={(k) => anthropicSaveMutation.mutate(k)}
        onDelete={() => anthropicDeleteMutation.mutate()}
        onSelectModel={(m) => prefsMutation.mutate({ anthropicModel: m })}
      />

      <div className="border-t border-gray-200 -mx-6 my-5" />

      <ProviderKeyCard
        embedded
        label="OpenAI API key"
        placeholder="sk-..."
        hint={{
          title: 'Optional',
          body: 'Route your AI calls through GPT on your own OpenAI bill. Get one at platform.openai.com → API keys. If you also save an Anthropic key (and no preference is set), Anthropic takes priority.',
        }}
        description="Save an OpenAI key to use your own OpenAI account for AI features. Encrypted at rest, never shared. Remove your Anthropic key (or set OpenAI as preferred) for this to become the active provider."
        hasKey={keys?.openai?.hasKey}
        preview={keys?.openai?.preview}
        isActive={keys?.active === 'openai' && keys?.openai?.source === 'user'}
        isOverridden={keys?.openai?.hasKey && keys?.active === 'anthropic'}
        createdAt={keys?.openai?.createdAt}
        lastUsedAt={keys?.openai?.lastUsedAt}
        model={keys?.openai?.model || options?.defaultOpenaiModel || ''}
        modelOptions={options?.openaiModels || []}
        saving={openaiSaveMutation.isPending}
        deleting={openaiDeleteMutation.isPending}
        onSave={(k) => openaiSaveMutation.mutate(k)}
        onDelete={() => openaiDeleteMutation.mutate()}
        onSelectModel={(m) => prefsMutation.mutate({ openaiModel: m })}
      />
    </div>
  );
}

function PreferencesForm({ preferred, enableAutoFallback, saving, onSave }) {
  const [pref, setPref] = useState(preferred);
  const [autoFallback, setAutoFallback] = useState(enableAutoFallback);

  useEffect(() => setPref(preferred), [preferred]);
  useEffect(() => setAutoFallback(enableAutoFallback), [enableAutoFallback]);

  const isDirty = pref !== preferred || autoFallback !== enableAutoFallback;
  const PROVIDER_LABEL = { anthropic: 'Anthropic (Claude)', openai: 'OpenAI (GPT)' };
  const savedSummary = preferred ? PROVIDER_LABEL[preferred] : 'No preference';
  const savedFallback = enableAutoFallback ? 'on' : 'off';

  return (
    <div className="space-y-3 text-sm">
      <div className="inline-flex items-center gap-2 text-xs bg-gray-50 border border-gray-100 rounded-md px-2.5 py-1.5">
        <span className="text-ink-500">Saved:</span>
        <span className="text-ink-700 font-medium">{savedSummary}</span>
        <span className="text-ink-400">·</span>
        <span className="text-ink-700">Auto-fallback {savedFallback}</span>
      </div>

      <div>
        <div className="font-medium mb-1">Preferred provider</div>
        <div className="flex gap-3 flex-wrap">
          {[
            { id: null, label: 'No preference' },
            { id: 'anthropic', label: 'Anthropic (Claude)' },
            { id: 'openai', label: 'OpenAI (GPT)' },
          ].map((opt) => (
            <label key={String(opt.id)} className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="preferred"
                checked={pref === opt.id}
                onChange={() => setPref(opt.id)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {pref === null && (
          <p className="text-xs text-ink-400 mt-1">
            With no preference, Anthropic wins when both keys are saved.
          </p>
        )}
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={autoFallback}
          onChange={(e) => setAutoFallback(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium">Enable auto-fallback</span>
          <span className="block text-xs text-ink-400">
            If the preferred provider fails (invalid key or no quota), automatically try the other
            one. Transient errors (rate-limit, network) are never auto-routed.
          </span>
        </span>
      </label>

      <div className="flex items-center gap-3">
        <button
          className="btn-primary"
          disabled={saving || !isDirty}
          onClick={() => onSave({ preferredAiProvider: pref, enableAutoFallback: autoFallback })}
        >
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
        {!isDirty && !saving && (
          <span className="text-xs text-ink-400">No unsaved changes</span>
        )}
      </div>
    </div>
  );
}
